import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Lock, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { useAIState } from '../contexts/AIStateContext';

// Route prefixes that expose AI-powered functionality. When a Manual Pro tenant
// (plan ai_enabled=false) lands on any of these, we render the page but lock it
// behind an "Upgrade to unlock AI" overlay. Single source keeps gating app-wide.
const AI_ROUTE_PREFIXES = [
  '/discover-leads',
  '/ai-workflows',
  '/ai-agents',
  '/brain',
  '/live-conversations',
];

const isAiRoute = (pathname) =>
  AI_ROUTE_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/') || pathname.startsWith(p + '?') || pathname === p);

export const AIGate = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { planAiEnabled, aiStatusLoaded, planName } = useAIState();

  const gated = aiStatusLoaded && planAiEnabled === false && isAiRoute(location.pathname);

  if (!gated) return children;

  return (
    <div className="relative" data-testid="ai-gate-locked">
      {/* The real panel — visible but blurred & non-interactive */}
      <div className="pointer-events-none select-none blur-[3px] opacity-50" aria-hidden="true">
        {children}
      </div>

      {/* Upgrade overlay */}
      <div className="absolute inset-0 z-20 flex items-start justify-center pt-16 sm:pt-24">
        <div className="mx-4 max-w-md w-full rounded-2xl border border-border bg-card/95 backdrop-blur-sm shadow-xl p-6 text-center"
          data-testid="ai-gate-upgrade-card">
          <div className="mx-auto h-12 w-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4">
            <Lock className="h-6 w-6" />
          </div>
          <h3 className="text-lg font-semibold flex items-center justify-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" /> AI is locked on {planName || 'your plan'}
          </h3>
          <p className="text-sm text-muted-foreground mt-2">
            You're on a manual CRM plan. Upgrade to an AI plan to unlock auto-replies,
            AI cadences, voice AI, lead reveal and the AI Brain — everything on this page.
          </p>
          <Button className="mt-5 w-full" onClick={() => navigate('/billing')} data-testid="ai-gate-upgrade-btn">
            Upgrade to unlock AI <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
          <button
            type="button"
            className="mt-3 text-xs text-muted-foreground hover:text-foreground underline"
            onClick={() => navigate('/dashboard')}
            data-testid="ai-gate-back-btn"
          >
            Back to dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIGate;
